<template>
  <div
    v-if="isComponentVisible"
    v-element-visibility="[
      onCarouselImpression,
      {
        threshold: 1,
        rootMargin: `${isMobile ? '-25%' : '-15%'} 0% 0%`,
      },
    ]"
    class="product-carousel-container"
  >
    <div class="product-carousel">
      <div class="product-carousel__content">
        <div class="product-carousel__description">
          <p class="display-m">{{ title }}</p>
          <p class="body-m">{{ subtitle }}</p>
        </div>
        <div v-if="ctaLabel && urlPath" class="product-carousel__actions">
          <UiMiniButton
            :text="ctaLabel"
            :url="urlPath"
            :is-new-tab="isOpenInNewTab"
            @clicked="onViewAllClick"
          />
        </div>
      </div>

      <div class="product-carousel__cards">
        <UiCarousel v-bind="carouselSettings">
          <template v-if="isPendingFeaturedProducts || isFetching">
            <UiCarouselSlide
              v-for="index in 5"
              :key="index"
              class="ssr-breakpoints-patch"
            >
              <ProductListingCardSkeleton />
            </UiCarouselSlide>
          </template>
          <template v-else>
            <UiCarouselSlide
              v-for="(product, index) in featuredProducts?.transformedData"
              :key="product.id"
              class="card-slide"
              @click="() => onTrackingProductClicked(index)"
            >
              <Suspense>
                <template #fallback>
                  <ProductListingCardSkeleton />
                </template>
                <ProductListingCardContainer
                  v-element-visibility="[
                    isVisible => onProductCardImpression(index, isVisible),
                    {
                      threshold: 1,
                      rootMargin: `${isMobile ? '-25%' : '-15%'} 0% 0%`,
                    },
                  ]"
                  :product-data="{
                    ...product,
                    ...recommendationAttributes,
                  }"
                  :product-card-index="index"
                  :carousel-name="internalName"
                />
              </Suspense>
            </UiCarouselSlide>
          </template>
        </UiCarousel>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UiCarousel, {
  ArrowPosition,
  NavArrowsDisplayType,
  UiCarouselSlide,
} from '@sephora-asia/ui-carousel'
import UiMiniButton from '@sephora-asia/ui-mini-button/UiMiniButton.vue'
import type { ProductCarouselContainerProps } from '~/types/components/contentful/components.types'
import { useQuery } from '@tanstack/vue-query'
import { useStorage } from '@vueuse/core'
import { vElementVisibility } from '@vueuse/components'
import { useGetFeaturedProductsQuery } from '~/composables/query/featured-products/use-featured-products-query'

const props = defineProps<ProductCarouselContainerProps>()

const { trackEvent } = useGtmTrack()
const pageMetaStore = usePageMetaStore()
const { isMobile } = useDevice()
const { normalizedRoutePath: pagePath } = useBaseRoutes()
const recentlyViewedProducts = useStorage<string[]>('PRODUCT_VIEWS', [])
const { countryCode } = useI18nLocale()
const { carouselOriginPageType, ga_session_id } = useTrackingHelper()
const { currentUser } = await useCurrentUser()

const payload = computed(() => ({
  id: props.id,
  pagePath: pagePath.value,
  recentlyViewedProducts: recentlyViewedProducts.value,
  ...props.productConfig,
}))

const productCardImpressionSet = ref(new Set<number>())

const {
  data: featuredProducts,
  isPending: isPendingFeaturedProducts,
  isFetching,
} = useQuery({
  ...useGetFeaturedProductsQuery(payload),
  enabled: import.meta.client, // Only fetch data on client side
})

const recommendationAttributes = computed(() => ({
  recommendationId:
    (featuredProducts.value?.transformedMeta?.recommendationId as string) || '',
  recommendationSplitKey:
    (featuredProducts.value?.transformedMeta
      ?.recommendationSplitKey as string) || '',
  recommendationStrategy:
    (featuredProducts.value?.transformedMeta
      ?.recommendationStrategy as string) || '',
}))

const isComponentVisible = computed(() => {
  if (props.productConfig?.listingType === 'native') return false
  if (isFetching.value || isPendingFeaturedProducts.value) return true
  return !!featuredProducts?.value?.transformedData.length
})

const carouselArrow = computed(() => ({
  position: ArrowPosition.WITHIN,
  displayType: isMobile
    ? NavArrowsDisplayType.HIDE
    : NavArrowsDisplayType.SHOW_ON_HOVER,
  size: '',
}))

const potionTrackingData = computed(() => ({
  origin: `${carouselOriginPageType.value}${recommendationAttributes.value.recommendationStrategy ? `|${recommendationAttributes.value.recommendationStrategy}` : ''}`,
  recommendation_id: recommendationAttributes.value.recommendationId,
  carousel_name: props.internalName,
  card_number: currentUser.value?.cardNumber,
  session_id: ga_session_id.value,
  page_path: pagePath.value,
  user_id: currentUser.value?.id,
  site_country: countryCode.toLowerCase(),
  traffic_split_key: recommendationAttributes.value.recommendationSplitKey,
}))

const carouselSettings = {
  slidesToShowCount: 5,
  slidesToScrollCount: 1,
  showDots: false,
  arrow: carouselArrow.value,
  isSwipeable: true,
  breakpoints: {
    0: { itemsToShow: 2.5, itemsToScroll: 2 },
    768: { itemsToShow: 5, itemsToScroll: 5 },
    1024: { itemsToShow: 3.5, itemsToScroll: 3 },
    1280: { itemsToShow: 4.5, itemsToScroll: 4 },
    1440: { itemsToShow: 5, itemsToScroll: 5 },
  },
}

function onViewAllClick() {
  trackEvent({
    event: `${pageMetaStore.pageType}_product_carousel_click_view_all`,
    params: {
      carousel_name: props.internalName,
      page_path: pagePath.value,
      segmented: props.isSegmented,
      segments: props.segments?.join(', '),
    },
  })
}

function onProductCardImpression(index: number, isVisible: boolean) {
  if (isVisible) {
    if (!productCardImpressionSet.value.has(index)) {
      trackEvent({
        ...potionTrackingData.value,
        event: 'recommended_for_you_card_impression',
        product_id: featuredProducts.value?.transformedData[index].id,
        variant_id:
          featuredProducts.value?.transformedData[index].featuredVariantId,
        card_sequence_number: index + 1,
      })
      productCardImpressionSet.value.add(index)
    }
  } else {
    productCardImpressionSet.value.delete(index)
  }
}

function onCarouselImpression(isVisible: boolean) {
  if (isVisible) {
    if (recommendationAttributes.value.recommendationId) {
      trackEvent({
        ...potionTrackingData.value,
        event: 'recommended_for_you_impression',
      })
    }
    trackEvent({
      event: `${pageMetaStore.pageType}_product_carousel_impression`,
      params: {
        carousel_name: props.internalName,
        content_banner_position: props.componentPosition,
        page_path: pagePath.value,
        segmented: props.isSegmented,
        segments: props.segments?.join(', '),
      },
    })
  }
}

function onTrackingProductClicked(index: number) {
  trackEvent({
    event: `${pageMetaStore.pageType}_product_carousel_click`,
    params: {
      carousel_name: props.internalName,
      content_banner_position: props.componentPosition,
      page_path: pagePath.value,
      product_click_position: index + 1,
      segmented: props.isSegmented,
      segments: props.segments?.join(', '),
    },
  })
}
</script>

<style lang="scss" scoped>
.product-carousel-container {
  padding-inline: 16px;

  .product-carousel {
    width: min(1320px, 100%);
    margin: auto;
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: center;

    &__content {
      width: 312px;
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;
      gap: 8px;

      @include media(mobile, tablet) {
        width: 100%;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }
    }

    &__actions {
      @include media(mobile, tablet) {
        flex: 1 0 auto;
        text-align: right;
      }
    }

    &__cards {
      width: 100%;
      overflow: hidden;

      .card-slide {
        text-align: left;
        gap: 16px;
        align-items: flex-start;
      }
    }

    @include media(mobile, tablet) {
      flex-direction: column;
      gap: 16px;
    }

    @each $breakpoint,
      $value
        in (mobile: 2.5, tablet: 5, desktop: 3.5, widescreen: 4.5, fullhd: 5)
    {
      :deep(.carousel__slide.ssr-breakpoints-patch) {
        @include media($breakpoint) {
          width: calc(100% / $value) !important;
        }
      }
    }
  }
}
</style>
